import { Injectable, type Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import Dexie, { liveQuery, type EntityTable } from "dexie";
import { type Observable, catchError, from, map, of } from "rxjs";

/**
 * Storage key enum (no magic strings)
 * Defines all keys used for application storage
 */
export enum StorageKeys {
	AuthToken = "auth_token",
	AuthUser = "auth_user",
	ThemePreference = "theme-preference",
}

/**
 * Storage entry interface
 * Represents a single key-value pair in the storage
 */
interface StorageEntry<T = any> {
	key: string;
	value: T;
	updatedAt: Date;
}

export type PhotoOutboxStatus = "queued" | "uploading" | "uploaded" | "failed";

export interface PhotoOutboxEntry {
	id: string;
	sessionId: string;
	roundId: string;
	itemId: string;
	blob: Blob;
	contentType: string;
	sizeBytes: number;
	status: PhotoOutboxStatus;
	attempts: number;
	nextAttemptAt: number;
	lastError?: string;
	photoKey?: string;
	submissionId?: string;
	broadcastAt?: number;
	createdAt: number;
	uploadedAt?: number;
}

/**
 * Dexie database class for Magpie application
 * Provides IndexedDB storage with typed schema
 */
export class MagpieDatabase extends Dexie {
	storage!: EntityTable<StorageEntry, "key">;
	photoOutbox!: EntityTable<PhotoOutboxEntry, "id">;

	constructor() {
		super("magpie-storage");
		this.version(1).stores({
			storage: "key, updatedAt",
		});
		this.version(2).stores({
			storage: "key, updatedAt",
			photoOutbox:
				"id, status, sessionId, [sessionId+roundId+itemId], nextAttemptAt",
		});
	}
}

let sharedDb: MagpieDatabase | null = null;
let sharedDbInitTried = false;

/**
 * Shared MagpieDatabase instance. Returns null in SSR or when IndexedDB
 * is unavailable. Lazy + memoised so every caller hits the same connection.
 */
export function getMagpieDb(): MagpieDatabase | null {
	if (sharedDbInitTried) return sharedDb;
	sharedDbInitTried = true;
	if (typeof indexedDB === "undefined") return null;
	try {
		sharedDb = new MagpieDatabase();
	} catch (error) {
		console.warn("getMagpieDb: Failed to initialize IndexedDB:", error);
		sharedDb = null;
	}
	return sharedDb;
}

/**
 * Storage Service
 *
 * Centralized storage service that wraps Dexie (IndexedDB) for better data persistence,
 * type safety, and reactive capabilities compared to localStorage.
 *
 * Features:
 * - SSR-safe with IndexedDB availability checks
 * - Automatic JSON serialization/deserialization
 * - Reactive updates via liveQuery$() Observable or liveQuerySignal() Signal
 * - Graceful error handling with console warnings
 * - TypeScript type safety for stored values
 *
 * @example
 * ```typescript
 * const storageService = inject(StorageService);
 *
 * // Store a value
 * await storageService.set(StorageKeys.AuthToken, 'my-token');
 *
 * // Retrieve a value
 * const token = await storageService.get<string>(StorageKeys.AuthToken);
 *
 * // React to changes with Observable
 * storageService.liveQuery$<string>(StorageKeys.AuthToken).subscribe(token => {
 *   console.log('Token changed:', token);
 * });
 *
 * // React to changes with Signal (Angular 20+)
 * const tokenSignal = storageService.liveQuerySignal<string>(StorageKeys.AuthToken);
 * effect(() => console.log('Token:', tokenSignal()));
 * ```
 */
@Injectable({
	providedIn: "root",
})
export class StorageService {
	private readonly db: MagpieDatabase | null = getMagpieDb();

	/**
	 * Get a value by key
	 * Returns undefined if the key is not found or if IndexedDB is unavailable
	 *
	 * @param key - Storage key to retrieve
	 * @returns Promise resolving to the stored value or undefined
	 */
	async get<T>(key: string): Promise<T | undefined> {
		if (!this.db) return undefined;

		try {
			const entry = await this.db.storage.get(key);
			return entry?.value as T | undefined;
		} catch (error) {
			console.warn(`StorageService: Failed to get key "${key}":`, error);
			return undefined;
		}
	}

	/**
	 * Set a value by key
	 * Stores the value with a timestamp. No-op if IndexedDB is unavailable.
	 *
	 * @param key - Storage key
	 * @param value - Value to store
	 */
	async set<T>(key: string, value: T): Promise<void> {
		if (!this.db) return;

		try {
			await this.db.storage.put({
				key,
				value,
				updatedAt: new Date(),
			});
		} catch (error) {
			console.warn(`StorageService: Failed to set key "${key}":`, error);
		}
	}

	/**
	 * Remove a value by key
	 * No-op if IndexedDB is unavailable
	 *
	 * @param key - Storage key to remove
	 */
	async remove(key: string): Promise<void> {
		if (!this.db) return;

		try {
			await this.db.storage.delete(key);
		} catch (error) {
			console.warn(`StorageService: Failed to remove key "${key}":`, error);
		}
	}

	/**
	 * Clear all stored values
	 * No-op if IndexedDB is unavailable
	 */
	async clear(): Promise<void> {
		if (!this.db) return;

		try {
			await this.db.storage.clear();
		} catch (error) {
			console.warn("StorageService: Failed to clear storage:", error);
		}
	}

	/**
	 * Get an observable that emits whenever the value for a key changes
	 * Uses Dexie's liveQuery for reactive updates
	 *
	 * @param key - Storage key to observe
	 * @returns Observable that emits the current value and any future changes
	 *
	 * @example
	 * ```typescript
	 * storageService.liveQuery$<string>(StorageKeys.AuthToken).subscribe(token => {
	 *   console.log('Current token:', token);
	 * });
	 * ```
	 */
	liveQuery$<T>(key: string): Observable<T | undefined> {
		if (!this.db) {
			return of(undefined);
		}

		// Convert Dexie's Observable to RxJS Observable to get access to pipe operators
		return from(liveQuery(() => this.db!.storage.get(key))).pipe(
			map((entry) => entry?.value as T | undefined),
			catchError((error) => {
				console.warn(
					`StorageService: liveQuery error for key "${key}":`,
					error,
				);
				return of(undefined);
			}),
		);
	}

	/**
	 * Get a signal that updates whenever the value for a key changes
	 * Uses Dexie's liveQuery converted to Angular Signal for reactive updates
	 *
	 * @param key - Storage key to observe
	 * @returns Signal that holds the current value and updates on changes
	 *
	 * @example
	 * ```typescript
	 * // In a component or service
	 * const tokenSignal = storageService.liveQuerySignal<string>(StorageKeys.AuthToken);
	 *
	 * // Use in template
	 * <div>Token: {{ tokenSignal() }}</div>
	 *
	 * // Or in an effect
	 * effect(() => {
	 *   console.log('Token changed to:', tokenSignal());
	 * });
	 * ```
	 */
	liveQuerySignal<T>(key: string): Signal<T | undefined> {
		return toSignal(this.liveQuery$<T>(key), { initialValue: undefined });
	}
}
